import axios from 'axios';

import { AddressDto, getGoogleMapsLoader, PropertyDto, PropertyLocationDto } from '@tallo/property';

export type UnitAddress = Pick<PropertyLocationDto, 'address' | 'city' | 'state' | 'zip'> & { apartmentNumber: string };

class AddressService {
  private googlePlacesClient: google.maps.places.AutocompleteSuggestion | null = null;
  private initializationPromise: Promise<void> | null = null;

  async getAddressDetails(address: string): Promise<AddressDto> {
    const response = await axios.get(`address-lookup/${address}`);

    return response.data;
  }

  /** The unit's own record, or null when there is none (the lookup only runs in production) */
  async getUnitDetails(unit: UnitAddress): Promise<Partial<PropertyDto> | null> {
    const response = await axios.put<Partial<PropertyDto> | ''>('address-lookup/property/details', unit);

    return response.data || null;
  }

  async addressAutocompleteGooglePlaces(query: string): Promise<AddressDto[]> {
    await this.ensureGooglePlacesInitialized();

    const predictions = await google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
      input: query,
      region: 'us',
    });

    if (!predictions || !predictions.suggestions) {
      return [];
    }

    const addresses: AddressDto[] = predictions.suggestions
      .filter(({ placePrediction }) => !!placePrediction?.mainText?.text)
      .map((prediction) => {
        const placePrediction = prediction.placePrediction as unknown as google.maps.places.PlacePrediction;

        return {
          address: placePrediction!.mainText?.text,
          city: placePrediction!.secondaryText?.text,
        } as AddressDto;
      });

    return addresses;
  }

  private async ensureGooglePlacesInitialized(): Promise<void> {
    if (this.googlePlacesClient) {
      return;
    }

    if (!this.initializationPromise) {
      this.initializationPromise = getGoogleMapsLoader()
        .importLibrary('places')
        .then(() => {
          this.googlePlacesClient = new google.maps.places.AutocompleteSuggestion();
        })
        .catch((error) => {
          console.error('Error loading Google Maps JavaScript API:', error);
          throw error;
        });
    }

    await this.initializationPromise;
  }
}

export default new AddressService();
